import React, { useState } from 'react';

import { IconTypes } from '@eika/icon';

import { useConfig } from '../../defaultConfig';
import type { Account } from '../../generated-types';
import usePaymentAccount from '../../hooks/usePaymentAccount';
import useTranslation from '../../hooks/useTranslation';
import { isValidAccountTransferFrom } from '../../services/accountFilters';
import { ActionButtonGroup } from '../shared/ActionButtonGroup';
import Payment from '../transaction/Payment/Payment';
import { Transfer } from '../transaction/Transfer';

interface Props {
    className?: string;
    account?: Account;
    withCrossborder?: boolean;
}

const AccountActions: React.FC<Props> = ({ className, account, withCrossborder = false }) => {
    const [showTransfer, setShowTransfer] = useState(false);
    const [showPayment, setShowPayment] = useState(false);
    const translated = useTranslation();
    const { crossBorderPaymentLink, accountDetailsLink, featureToggles } = useConfig();

    const paymentAccount = usePaymentAccount(showTransfer ? isValidAccountTransferFrom : undefined);
    const usedAccount = account ?? paymentAccount;
    const actions = [
        {
            children: translated.payment,
            icon: IconTypes.LOMMEBOK_36,
            onClick: () => setShowPayment(true),
            show: !account ? true : account.rights.paymentFrom,
        },
        {
            children: translated.transfer,
            icon: IconTypes.OVERFOERE_36,
            onClick: () => setShowTransfer(true),
            show: !account ? true : account.rights.transferFrom,
        },
        {
            children: translated.more,
            href: `${accountDetailsLink?.url}?id=${account?.accountId.value}`,
            icon: IconTypes.MER_36,
            isSecondary: true,
            hiddenOnDesktop: true,
            'data-testid': 'account-details-link',
            show: !withCrossborder && account && accountDetailsLink,
        },
        {
            children: translated.payToAForeignCountry,
            icon: IconTypes.JORDEN_36,
            href: crossBorderPaymentLink?.url,
            show: crossBorderPaymentLink?.url && withCrossborder && !featureToggles.disableCrossBorderPayments,
        },
    ].filter((action) => action.show);

    const onCloseTransfer = (openPayment: boolean) => {
        if (openPayment) {
            setShowPayment(true);
            setTimeout(() => {
                // wait til payment modal is shown to avoid too much animation on screen.
                setShowTransfer(false);
            }, 1000);
        } else {
            setShowTransfer(false);
        }
    };

    if (actions.length === 0) {
        return undefined;
    }

    return (
        <>
            <ActionButtonGroup actions={actions} className={className} />
            <Transfer account={usedAccount} show={showTransfer} setShow={setShowTransfer} onClose={onCloseTransfer} />
            {showPayment && <Payment account={usedAccount} show={showPayment} setShow={setShowPayment} />}
        </>
    );
};

export default AccountActions;
